import React from 'react';
import { useMethods } from '@lhb/hook';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Empty from '@/common/components/Data/V2Empty';
import V2LineChart from '@/common/components/Charts/V2LineChart';
import V2BarLineChart from '@/common/components/Charts/V2BarLineChart';
import dayjs from 'dayjs';
import Statistic from './Statistic';
import levelIcon from './assets/level.svg';
import rankIcon from './assets/rank.svg';
import workdayIcon from './assets/workday.svg';
import holidayIcon from './assets/holiday.svg';
import { MallData, Metric, SeriesPoint } from '../../types';
import MallFloorMap from '../MallFloorMap';
import styles from './index.module.less';

const MallSection: React.FC<{ data: MallData }> = ({ data }) => {
  const methods = useMethods({
    metric(item: Metric, index: number) {
      let value = item.value;
      let hint = '';
      if (item.label === '商场等级' && value !== null) {
        const parts = String(value).split(' · ');
        value = parts[0].startsWith('评分') || parts[0].endsWith('级') ? parts[0] : `${parts[0]}级`;
        hint = parts.slice(1).join(' · ');
        if (hint) hint = `${hint.startsWith('评分') ? hint : `评分 ${hint}`} 数据部门`;
      }
      if (item.label === '工作日客流' || item.label === '节假日客流') {
        const count = Number(String(value).replace('人/日', ''));
        if (value !== null && Number.isFinite(count)) value = count >= 1000 ? `${(count / 1000).toFixed(1)}k` : String(count);
      }
      return <Statistic key={item.label} label={item.label === '工作日客流' || item.label === '节假日客流' ? `日均${item.label}` : item.label} value={value} hint={hint} icon={[levelIcon, rankIcon, workdayIcon, holidayIcon][index]} />;
    },
    axisValue(value: number) { return Math.abs(value) >= 10000 ? `${Number((value / 10000).toFixed(1))}w` : String(value); },
    dayLabel(value: string) { return /^\d{4}-\d{2}-\d{2}$/.test(value) ? dayjs(value).format('MMDD') : value; },
    dateRange() {
      const start = data.daily[0]?.label;
      const end = data.daily[data.daily.length - 1]?.label;
      return start && end ? `${dayjs(start).format('YYYY.MM.DD')} - ${dayjs(end).format('YYYY.MM.DD')}` : '';
    },
    label(item: SeriesPoint) { return item.label; },
    value(item: SeriesPoint) { return item.value; },
    available(item: SeriesPoint) { return item.value !== null; },
    monthLabel(item: MallData['monthly'][number]) { return item.label; },
    baseline(item: MallData['monthly'][number]) { return item.baseline; },
    comparison(item: MallData['monthly'][number]) { return item.comparison; },
    share(item: MallData['monthly'][number]) { return item.share; },
    monthAvailable(item: MallData['monthly'][number]) { return item.baseline !== null || item.comparison !== null || item.share !== null; },
  });
  const monthlySeries = [
    { name: '基准月日均客流', type: 'bar', data: data.monthly.map(methods.baseline), barMaxWidth: 12, itemStyle: { color: '#3986ff' } },
    { name: '对比月日均客流', type: 'bar', data: data.monthly.map(methods.comparison), barMaxWidth: 12, itemStyle: { color: '#39ccee' } },
    { name: '全市客流占比', type: 'line', data: data.monthly.map(methods.share), yAxisIndex: 1, connectNulls: false, showSymbol: false, unit: '%', itemStyle: { color: '#ffca55' }, lineStyle: { color: '#ffca55', width: 1.5 } },
  ];
  const hasDaily = data.daily.some(methods.available);
  const hasMonthly = data.monthly.some(methods.monthAvailable);
  return <section className={styles.root}>
    <V2Title type='H2' text='商场情况' bold innerStyle={{ fontSize: 18, fontWeight: 600, lineHeight: '24px', color: '#222' }} />
    <div className={styles.metrics}>{data.metrics.length ? data.metrics.map(methods.metric) : <V2Empty />}</div>
    {(hasDaily || hasMonthly) && <div className={styles.chartsGrid}>
      <div className={styles.chart}>
        <V2Title type='H2' text='日客流趋势' bold extra={<span className={styles.dateRange}>{methods.dateRange()}</span>} />
        {hasDaily ? <V2LineChart resizable height={222} xAxisData={data.daily.map(methods.label)}
          seriesData={[{ name: '日客流', data: data.daily.map(methods.value), connectNulls: false, showSymbol: false, lineStyle: { color: '#579aff', width: 2 }, itemStyle: { color: '#579aff' },
            markLine: data.dailyAverage === null ? undefined : { symbol: 'none', lineStyle: { type: 'dashed', color: '#ffc136', width: 1 }, data: [{ name: '日均客流', yAxis: data.dailyAverage }], label: { formatter: '日均', position: 'insideEndTop', color: '#fff', backgroundColor: '#ffc136', borderRadius: 8, padding: [2, 4], fontSize: 11 } } }]}
          config={{ legend: { show: false }, tooltip: { trigger: 'axis' }, grid: { top: 16, left: 8, right: 16, bottom: 8, containLabel: true }, xAxis: { axisLabel: { formatter: methods.dayLabel, color: '#999', fontSize: 12, hideOverlap: true } }, yAxis: { axisLabel: { formatter: methods.axisValue, color: '#999', fontSize: 12 }, splitLine: { lineStyle: { color: '#eee', type: 'dashed' } } } }} /> : <V2Empty />}
      </div>
      <div className={styles.chart}>
        <V2Title type='H2' text='月日均客流趋势' bold extra={<div className={styles.legend}>
          <span className={styles.legendItem}><i style={{ background: '#3986ff' }} />基准月日均客流</span>
          <span className={styles.legendItem}><i style={{ background: '#39ccee' }} />对比月日均客流</span>
          <span className={styles.legendItem}><i style={{ background: '#ffca55' }} />全市客流占比</span>
        </div>} />
        {hasMonthly ? <V2BarLineChart resizable height={222} xAxisData={data.monthly.map(methods.monthLabel)} seriesData={monthlySeries} yAxisData={[{ axisLabel: { formatter: methods.axisValue, color: '#999', fontSize: 12 }, splitLine: { lineStyle: { color: '#eee', type: 'dashed' } } }, { axisLabel: { formatter: '{value}%', color: '#999', fontSize: 12 }, splitLine: { show: false } }]} config={{ grid: { top: 16, bottom: 8, left: 8, right: 8, containLabel: true }, legend: { show: false }, tooltip: { trigger: 'axis' }, xAxis: { axisLabel: { color: '#999', fontSize: 12, hideOverlap: true } } }} /> : <V2Empty />}
      </div>
    </div>}
    <MallFloorMap data={data.indoor} />
  </section>;
};
export default MallSection;
